import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import BeatsToggleIcon from './icons/BeatsToggleIcon';

interface BeatsToggleProps {
  showBeats: boolean;
  /** The song has no time signature: the switch is dimmed and opens the time-signature editor. */
  needsTimeSignature: boolean;
  onToggleBeats: () => void;
}

/** Header switch for beat dots and the beats button, after the Lyrics switch (RN: Display menu → Beats). */
const BeatsToggle: React.FC<BeatsToggleProps> = ({ showBeats, needsTimeSignature, onToggleBeats }) => {
  const { theme } = useTheme();
  const isOn = showBeats && !needsTimeSignature;
  const label = needsTimeSignature
    ? 'Beats need a time signature: set one'
    : isOn ? 'Hide beats' : 'Show beats';

  return (
    <button
      onClick={onToggleBeats}
      style={{
        width: '32px',
        height: `${theme.spacing.xl - 4}px`, // 20px, like the other switches
        borderRadius: `${theme.radii.full}px`,
        padding: '2px',
        position: 'relative',
        backgroundColor: isOn ? theme.colors.beats : theme.colors.textSecondary,
        opacity: needsTimeSignature ? theme.opacity.disabled : 1,
        border: 'none',
        cursor: 'pointer',
        transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
      }}
      aria-label={label}
      aria-pressed={isOn}
      title={label}
      data-testid="beats-toggle"
    >
      <div
        style={{
          width: `${theme.spacing.lg}px`,
          height: `${theme.spacing.lg}px`,
          borderRadius: `${theme.radii.lg}px`,
          backgroundColor: theme.colors.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          left: isOn ? 'calc(100% - 18px)' : '2px',
          top: '2px',
          transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
        }}
      >
        <BeatsToggleIcon size={12} color={isOn ? theme.colors.beats : theme.colors.textTertiary} />
      </div>
    </button>
  );
};

export default BeatsToggle;
